import React, {useEffect, useMemo} from 'react';
import {MapContainer, TileLayer} from 'react-leaflet';
import HeatmapLayer from './HeatmapLayer';
import './leaflet.css';
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {findMostCommonCoordinates} from "../../utils/getCenterAlert";
import { useTranslation } from 'react-i18next';
import { Skeleton, Fade, IconButton, Chip } from '@mui/material';
import { MapPin, Layers, ZoomIn, ZoomOut } from 'lucide-react';

// Add custom styles for the map
const mapContainerStyle = {
  height: "calc(100% - 24px)",
  width: "100%",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
};

// Custom CSS to apply rounded corners to leaflet internal elements
const customMapStyle = `
  .leaflet-container {
    border-radius: 12px;
    overflow: hidden;
    font-family: 'Inter', sans-serif;
  }
  .leaflet-control-container .leaflet-control {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border: none;
  }
  .leaflet-control-zoom a {
    width: 32px;
    height: 32px;
    line-height: 32px;
    font-size: 16px;
    color: #333;
    background: rgba(255, 255, 255, 0.95);
    border: none;
    transition: all 0.2s ease;
  }
  .leaflet-control-zoom a:hover {
    background: #fff;
    transform: scale(1.05);
  }
  .leaflet-control-attribution {
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(10px);
    padding: 4px 8px;
    font-size: 11px;
  }
  .leaflet-popup-content-wrapper {
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }
  .leaflet-popup-tip {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`;

interface DataPoint {
    latitude: number | null;
    longitude: number | null;
    intensity?: number;
}

interface HeatmapProps {
    data: DataPoint[];
}

export const Heatmap: React.FC<HeatmapProps> = ({data}) => {
    // Filter out points with null latitude or longitude using useMemo to prevent infinite loops
    const validData = useMemo(() => {
        return data.filter(item =>
            item.latitude !== null &&
            item.longitude !== null &&
            typeof item.latitude === 'number' &&
            typeof item.longitude === 'number'
        );
    }, [data]);

    const points: [number, number, number][] = useMemo(() => {
        // Créer plusieurs points autour de chaque alerte pour intensifier la zone de chaleur
        const enhancedPoints: [number, number, number][] = [];

        validData.forEach(item => {
            const lat = item.latitude!;
            const lng = item.longitude!;
            const intensity = item.intensity || 1;

            // Ajouter le point principal
            enhancedPoints.push([lat, lng, intensity]);

            // Ajouter des points additionnels autour pour créer une zone de chaleur
            // Ceci simule une zone d'impact plus large
            const offset = 0.001; // ~100m
            enhancedPoints.push([lat + offset, lng, intensity * 0.7]);
            enhancedPoints.push([lat - offset, lng, intensity * 0.7]);
            enhancedPoints.push([lat, lng + offset, intensity * 0.7]);
            enhancedPoints.push([lat, lng - offset, intensity * 0.7]);
        });

        return enhancedPoints;
    }, [validData]);

    const [center, setCenter] = React.useState<[number, number]>([48.8583694, 2.2481174])
    const [centerIsLoading, setCenterIsLoading] = React.useState<boolean>(true)

    const {t} = useTranslation();

    useEffect(() => {
        if (validData.length > 0) {
            setCenterIsLoading(true)
            const findCenter = findMostCommonCoordinates(validData as any)
            // console.log("center", findCenter)
            setCenter([findCenter?.latitude || 48.8583694, findCenter?.longitude || 2.2481174])
            setCenterIsLoading(false)
        } else {
            setCenterIsLoading(false)
        }
    }, [validData])

    return (
      <Box
        sx={{
          height: 400,
          p: 3,
          background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
          borderRadius: 3,
          overflow: "hidden",
          width: "100%",
          position: "relative",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 15px 40px rgba(0, 0, 0, 0.15)",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at top right, rgba(255, 255, 255, 0.1) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2, position: "relative", zIndex: 1 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <MapPin size={40} color="rgba(255, 255, 255, 0.9)" />
            <Box>
              <Typography
                variant="h5"
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  mb: 0.5,
                  fontFamily: "Inter, sans-serif",
                  letterSpacing: "-0.01em",
                }}
              >
                {t("dashboard.heatmap")}
              </Typography>
              {validData.length > 0 && (
                <Fade in={!centerIsLoading} timeout={800}>
                  <Chip
                    label={`${validData.length} ${t("dashboard.locations")}`}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      color: "#fff",
                      fontWeight: 500,
                      backdropFilter: "blur(10px)",
                      "& .MuiChip-label": {
                        px: 1.5,
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                </Fade>
              )}
            </Box>
          </Box>
          <IconButton
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(10px)",
              color: "white",
              width: 36,
              height: 36,
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.25)",
                transform: "scale(1.1)",
              },
            }}
          >
            <Layers size={18} />
          </IconButton>
        </Stack>
        {centerIsLoading ? (
          <Box
            sx={{
              height: "calc(100% - 2rem)",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.1)",
                borderRadius: 3,
              }}
            />
          </Box>
        ) : validData.length === 0 ? (
          <Fade in={true} timeout={800}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "calc(100% - 2rem)",
                color: "#fff",
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                borderRadius: 3,
                border: "2px dashed rgba(255, 255, 255, 0.2)",
              }}
            >
              <MapPin
                size={48}
                color="rgba(255, 255, 255, 0.5)"
                style={{ marginBottom: 16 }}
              />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {t("dashboard.noLocationData")}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.7)" }}
              >
                {t("dashboard.noLocationDataDesc")}
              </Typography>
            </Box>
          </Fade>
        ) : (
          <Fade in={!centerIsLoading} timeout={1000}>
            <Box sx={{ height: "calc(100% - 3rem)", position: "relative" }}>
              <style>{customMapStyle}</style>
              <MapContainer
                center={center}
                zoom={13}
                style={mapContainerStyle}
                className="rounded-map"
                zoomControl={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url={`https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${process.env.REACT_APP_STADIA_API_KEY}`}
                  minZoom={0}
                  maxZoom={20}
                  opacity={0.9}
                />
                <HeatmapLayer points={points} />
              </MapContainer>

              {/* Custom Zoom Controls */}
              <Box
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  zIndex: 1000,
                }}
              >
                <IconButton
                  onClick={() => {
                    const mapElement =
                      document.querySelector(".leaflet-container");
                    if (mapElement && (mapElement as any)._leaflet_map) {
                      (mapElement as any)._leaflet_map.zoomIn();
                    }
                  }}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    width: 40,
                    height: 40,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    "&:hover": {
                      backgroundColor: "#fff",
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <ZoomIn size={20} />
                </IconButton>
                <IconButton
                  onClick={() => {
                    const mapElement =
                      document.querySelector(".leaflet-container");
                    if (mapElement && (mapElement as any)._leaflet_map) {
                      (mapElement as any)._leaflet_map.zoomOut();
                    }
                  }}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    width: 40,
                    height: 40,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    "&:hover": {
                      backgroundColor: "#fff",
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <ZoomOut size={20} />
                </IconButton>
              </Box>
            </Box>
          </Fade>
        )}
      </Box>
    );
};
